import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import { startOfDay } from 'date-fns';
import { isWork, type Range } from './stats';
import { sortTasks } from './tasks';
import type { AgentRun, Label, Project, Session, Task } from '@timato/shared';
import { db } from '@/db/db';
import { notDeleted } from './repo';
import { useCore } from './core';

// The four below come from the core store, which subscribes once for the whole session (see core.ts).

/** Settings row, with defaults until the row exists. */
export const useSettings = () => useCore((s) => s.settings);

/** The single active-timer row, idle until the first Start. */
export const useActiveTimer = () => useCore((s) => s.activeTimer);

export const useProjects = (): Project[] => useCore((s) => s.projects);

export const useProjectMap = () => {
  const projects = useProjects();
  return useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
};

export const useTask = (taskId: string | null): Task | undefined =>
  useLiveQuery(() => (taskId ? db.tasks.get(taskId) : undefined), [taskId]);

/**
 * Work sessions (focus + track, not deleted) that started on a given day.
 *
 * `undefined` while the first read is in flight. The list hooks below say the same thing: a screen that
 * defaults to an empty array cannot tell "nothing logged" from "not read yet", and paints the empty state
 * for a frame before the rows arrive.
 */
export const useDaySessions = (day: Date): Session[] | undefined => {
  const from = startOfDay(day).toISOString();
  const to = new Date(startOfDay(day).getTime() + 86_400_000).toISOString();
  return useLiveQuery(
    async () => (await db.sessions.where('startedAt').between(from, to, true, false).toArray()).filter(notDeleted),
    [from, to],
  );
};

export { isWork };

/** All not-deleted sessions in a half-open range; `undefined` until the first read lands. */
export const useRangeSessions = ({ from, to }: Range): Session[] | undefined =>
  useLiveQuery(
    async () => (await db.sessions.where('startedAt').between(from.toISOString(), to.toISOString(), true, false).toArray()).filter(notDeleted),
    [from.getTime(), to.getTime()],
  );

export const useProject = (id: string | null | undefined): Project | undefined => {
  const projects = useProjects();
  return id ? projects.find((p) => p.id === id) : undefined;
};

/** A project's tasks, open first by position, finished last; `undefined` until the first read lands. */
export const useProjectTasks = (projectId: string | null | undefined): Task[] | undefined =>
  useLiveQuery(async () => (projectId ? sortTasks((await db.tasks.where('projectId').equals(projectId).toArray()).filter(notDeleted)) : []), [projectId]);

/** A finished run stays on the timer screen this long, so you notice it without being interrupted (9a). */
export const FINISHED_VISIBLE_MIN = 15;

/**
 * Agent runs worth showing on the timer screen: everything in flight, plus anything that finished in the last
 * quarter of an hour. `undefined` until the first read lands.
 */
export const useTimerRuns = (now: Date): AgentRun[] | undefined => {
  const since = new Date(now.getTime() - FINISHED_VISIBLE_MIN * 60_000).toISOString();
  return useLiveQuery(async () => {
    const rows = (await db.agentRuns.toArray()).filter(notDeleted);
    return rows
      .filter((r) => !r.endedAt || r.endedAt >= since)
      .sort((a, b) => Number(!!a.endedAt) - Number(!!b.endedAt) || b.startedAt.localeCompare(a.startedAt));
  }, [since]);
};

/** Finished agent runs that started inside a range, for the optional layer on the week chart (9h). */
export const useRangeRuns = ({ from, to }: Range): AgentRun[] | undefined =>
  useLiveQuery(async () => (await db.agentRuns.where('startedAt').between(from.toISOString(), to.toISOString(), true, false).toArray()).filter(notDeleted),
    [from.getTime(), to.getTime()]);

/** Names for a set of task ids, since a run may outlive the task it was on. */
export const useTaskNames = (ids: string[]): Map<string, string> => {
  const key = [...new Set(ids)].sort().join(',');
  return useLiveQuery(async () => {
    const rows = key ? await db.tasks.bulkGet(key.split(',')) : [];
    return new Map(rows.filter((t) => !!t).map((t) => [t.id, t.name]));
  }, [key], new Map<string, string>()) ?? new Map<string, string>();
};

/** Finished runs on a task or project, for the figures that sit beside the person's time (9c, 9d). */
export const useRunsForTask = (taskId: string | null | undefined): AgentRun[] | undefined =>
  useLiveQuery(async () => (taskId ? (await db.agentRuns.where('taskId').equals(taskId).toArray()).filter(notDeleted) : []), [taskId]);

export const useRunsForProject = (projectId: string | null | undefined): AgentRun[] | undefined =>
  useLiveQuery(async () => (projectId ? (await db.agentRuns.where('projectId').equals(projectId).toArray()).filter(notDeleted) : []), [projectId]);

/** How far back to look for "what was I last on". Plenty for a shortlist; one indexed read either way. */
const RECENT_SESSIONS = 200;
const NO_RECENT: ReadonlyMap<string, number> = new Map();

/**
 * Task ids by how recently they were worked, most recent first (0 is the most recent).
 * Tasks carry a total of time tracked but not a last-worked stamp, so it is derived from sessions.
 */
export const useRecentTaskIds = (): ReadonlyMap<string, number> =>
  useLiveQuery(async () => {
    const recent = await db.sessions.orderBy('startedAt').reverse().limit(RECENT_SESSIONS).toArray();
    const rank = new Map<string, number>();
    for (const s of recent) if (s.taskId && notDeleted(s) && !rank.has(s.taskId)) rank.set(s.taskId, rank.size);
    return rank;
  }, [], NO_RECENT) ?? NO_RECENT;

/** Open (unfinished) tasks across all active projects, for the goal combobox; `undefined` until first read. */
export const useOpenTasks = (): Task[] | undefined =>
  useLiveQuery(async () => sortTasks((await db.tasks.toArray()).filter((t) => notDeleted(t) && !t.finishedAt)), []);

export const useLabelMap = () => {
  const labels = useLabels();
  return useMemo(() => new Map(labels.map((l) => [l.id, l])), [labels]);
};

/** All not-deleted labels (personal scope in Phase 1), by position. */
export const useLabels = (): Label[] => useCore((s) => s.labels);
